import type { MindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { fetchDashboardWorksheets as fetchDashboardWorksheetsAction } from '../../../actions/dashboard.actions';
import {
	getDashboardWorksheets,
	getDashboardWorksheetsStatus,
	getDashboardWorksheetsSubjectsKey,
} from '../../../reducers/dashboard.selectors';
import useAction from '../../../utils/useAction';
import useDashboardRowFetch, { MAX_ITEMS } from './useDashboardRowFetch';

export interface DashboardWorksheets {
	items: MindItemSearchResult[];
	status: AsyncActionStatus;
}

/**
 * Drives the dashboard worksheet row: reads the dashboard-scoped worksheet slice,
 * fetches once when unloaded, refetches when the subject set changes, and skips a
 * remount that already holds the same subjects. Returns items capped at MAX_ITEMS.
 */
const useDashboardWorksheets = (subjects: string[]): DashboardWorksheets => {
	const fetchDashboardWorksheets = useAction(fetchDashboardWorksheetsAction);

	const items = useSelector(getDashboardWorksheets);
	const status = useSelector(getDashboardWorksheetsStatus);
	const loadedKey = useSelector(getDashboardWorksheetsSubjectsKey);

	const fetchKey = subjects.join(',');

	useDashboardRowFetch({
		fetchKey,
		status,
		loadedKey,
		enabled: subjects.length > 0,
		fetch: () => fetchDashboardWorksheets(subjects),
	});

	// The single worksheet slot still holds the previous subjects' data/status until
	// the refetch's START lands. Suppress those stale cards AND report an effective
	// LOADING while the loaded key lags behind, so the row shows skeletons through the
	// transition. This applies to every stale status — including ERROR: a stale key
	// means useDashboardRowFetch is already firing a fresh request for the new
	// subjects, so the previous subjects' outcome must not leak as a settled frame.
	const staleKey = loadedKey !== fetchKey;
	const itemsForKey = staleKey ? [] : items.slice(0, MAX_ITEMS);
	const effectiveStatus = staleKey ? AsyncActionStatus.LOADING : status;

	return { items: itemsForKey, status: effectiveStatus };
};

export default useDashboardWorksheets;
